import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Handshake, User, Mail, Phone, Briefcase, ArrowRight, CheckCircle2, TrendingUp, Lock, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import type { PromoterLegalStatus } from '../types/promoter';
import { AuthModal } from '../components/AuthModal';

const LEGAL_OPTIONS: { id: PromoterLegalStatus; label: string; desc: string }[] = [
  { id: 'auto', label: 'Auto-entrepreneur', desc: 'Micro-entreprise / freelance' },
  { id: 'company', label: 'Société', desc: 'SAS, SARL, EURL…' },
  { id: 'individual', label: 'Particulier', desc: 'Sans statut pro (versement en carte cadeau)' },
];

export default function BecomePromoterPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [context, setContext] = useState('');
  const [legalStatus, setLegalStatus] = useState<PromoterLegalStatus | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');

  useEffect(() => {
    if (user?.email) setEmail(prev => prev || user.email || '');
  }, [user]);

  // Pré-remplit et détecte une candidature existante
  useEffect(() => {
    async function check() {
      if (!user) return;
      try {
        const snap = await getDoc(doc(db, 'promoters', user.uid));
        if (snap.exists()) setAlreadyApplied(snap.data().status);
      } catch { /* rules : non-owner ne lit pas — ignore */ }
    }
    check();
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) { toast.error('Connectez-vous pour candidater'); return; }
    if (!firstName || !lastName || !email || !phone || !legalStatus) {
      toast.error('Merci de remplir tous les champs requis');
      return;
    }
    setSubmitting(true);
    try {
      const token = await user.getIdToken();
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/promoters/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ firstName, lastName, email, phone, context, legalStatus }),
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.error || 'Erreur');
      setDone(true);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Écran : candidature soumise ou déjà existante ──
  if (done || alreadyApplied) {
    const status = alreadyApplied || 'pending';
    return (
      <div className="min-h-screen bg-[#0D0D0F] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-black mb-3">
            {status === 'active' ? 'Vous êtes déjà promoteur !' :
             status === 'rejected' ? 'Candidature déjà examinée' :
             'Candidature envoyée !'}
          </h1>
          <p className="text-gray-400 text-sm leading-relaxed mb-8">
            {status === 'active'
              ? 'Votre profil promoteur est validé. Rendez-vous sur votre dashboard.'
              : status === 'rejected'
              ? 'Votre candidature précédente n\'a pas été retenue. Contactez-nous si votre situation a évolué.'
              : 'Merci ! Notre équipe examine votre candidature et reviendra vers vous sous 48h ouvrées. Un email de confirmation vient de vous être envoyé.'}
          </p>
          <button
            onClick={() => navigate(status === 'active' ? '/promoter/dashboard' : '/home')}
            className="bg-[#FF5C00] hover:bg-[#e05000] text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-all"
          >
            {status === 'active' ? 'Accéder à mon dashboard' : 'Retour à l\'accueil'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0F] text-white">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#FF5C00] opacity-10 blur-[140px] rounded-full pointer-events-none" />
        <div className="relative max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="inline-flex items-center gap-2 bg-[#FF5C00]/10 border border-[#FF5C00]/20 text-[#FF5C00] text-xs font-black px-3 py-1.5 rounded-full mb-6">
            <Handshake className="w-3.5 h-3.5" /> PROGRAMME AFFILIATION
          </div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-4">Devenez promoteur Festin</h1>
          <p className="text-gray-400 text-base leading-relaxed max-w-lg mx-auto">
            Apportez des festivals à Festin et percevez <strong className="text-white">20% de commission</strong> sur les revenus générés. Un code affilié, un dashboard temps réel, des versements suivis.
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10">
        {/* Avantages */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
          {[
            { icon: <TrendingUp className="w-4 h-4 text-[#FF5C00]" />, t: '20% de commission', d: 'Sur les revenus Festin, 12 mois' },
            { icon: <Sparkles className="w-4 h-4 text-[#FF5C00]" />, t: 'Code affilié unique', d: 'Tracking automatique' },
            { icon: <CheckCircle2 className="w-4 h-4 text-[#FF5C00]" />, t: 'Dashboard temps réel', d: 'Leads, statuts, commissions' },
          ].map(b => (
            <div key={b.t} className="bg-[#161618] border border-white/8 rounded-2xl p-4">
              <div className="mb-2">{b.icon}</div>
              <p className="text-sm font-black text-white">{b.t}</p>
              <p className="text-xs text-gray-500 mt-0.5">{b.d}</p>
            </div>
          ))}
        </div>

        {/* Gate : connexion requise — modale ouverte directement sur cette page
            (avant, on renvoyait vers la landing sans rien ouvrir : l'utilisateur
            devait retrouver lui-même le bouton connexion, trou de conversion). */}
        {!authLoading && !user ? (
          <div className="bg-[#161618] border border-white/8 rounded-2xl p-8 text-center">
            <Lock className="w-8 h-8 text-[#FF5C00] mx-auto mb-4" />
            <h2 className="text-lg font-black mb-2">Connectez-vous pour candidater</h2>
            <p className="text-sm text-gray-400 mb-6">La candidature promoteur est liée à votre compte Festin.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => {
                  // Marqueur lu par VerifyEmail.tsx pour revenir ici après vérification
                  // au lieu du tunnel de création de festival (rôle 'festival' forcé
                  // uniquement pour la structure du compte, pas l'intention réelle).
                  localStorage.setItem('festin_pending_promoter_signup', '1');
                  setAuthMode('signup'); setShowAuthModal(true);
                }}
                className="w-full sm:w-auto bg-[#FF5C00] hover:bg-[#e05000] text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-all"
              >
                Créer un compte
              </button>
              <button
                onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
                className="w-full sm:w-auto bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-all"
              >
                Se connecter
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Prénom / Nom */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Prénom *</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5C00]/50" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Nom *</label>
                <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5C00]/50" />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Email *</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5C00]/50" />
              </div>
            </div>

            {/* Téléphone */}
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Téléphone *</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} required
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5C00]/50" />
              </div>
            </div>

            {/* Statut juridique */}
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Statut juridique *</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {LEGAL_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setLegalStatus(opt.id)}
                    className={`text-left rounded-xl border p-3 transition-all ${
                      legalStatus === opt.id
                        ? 'border-[#FF5C00] bg-[#FF5C00]/10'
                        : 'border-white/10 bg-white/3 hover:border-white/20'
                    }`}
                  >
                    <p className={`text-sm font-black ${legalStatus === opt.id ? 'text-[#FF5C00]' : 'text-white'}`}>{opt.label}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Expérience / contexte */}
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                Expérience / contexte <span className="text-gray-600 normal-case font-medium">(optionnel)</span>
              </label>
              <div className="relative">
                <Briefcase className="absolute left-4 top-4 w-4 h-4 text-gray-500" />
                <textarea
                  value={context}
                  onChange={e => setContext(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Comment avez-vous entendu parler de Festin ? Avec quels festivals êtes-vous en contact ?"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#FF5C00]/50 resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-3 bg-[#FF5C00] hover:bg-[#e05000] disabled:opacity-40 text-white py-4 rounded-2xl font-black text-sm transition-all"
            >
              {submitting ? 'Envoi en cours…' : 'Envoyer ma candidature'}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>
            <p className="text-center text-[11px] text-gray-600">
              Votre profil sera examiné sous 48h. L'accès au dashboard nécessite la signature du contrat d'apporteur d'affaires.
            </p>
          </form>
        )}
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
        role="festival"
      />
    </div>
  );
}
